import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import SourceTags from './components/SourceTags';
import ImgTag from './components/ImgTag';
import DEFAULT_IMG_TAG_FALLBACK_CONFIG from './constants';

/**
 * This component accepts an Array of objects representing `images` and other configuration parameters and renders a <picture> element with relevant <source> tags and an <img> tag
 * @param {Array} images Array of objects representing images e.g. [{ file: [FILEDATA], format: 'webp', resolution: '2x', 'size: { id: 'lg', value: 'large', minVw: '900px' }}]
 * @param {String} altText String to use as image alt text (optional)
 * @param {Object} imgTagFallbackConfig Overrides the DEFAULT_IMG_TAG_FALLBACK_CONFIG which selects the image to use as the fallback img src (optional)
 * @param {String} className Classname to apply to `<img />` (optional)
 * @param {Boolean} isLazy Indicates if image loading attribute should be set to 'lazy' or 'eager' (optional, defaults to false => 'eager')
 * @param {String} dataCy Cypress test id to be applied to `<img />` (optional)
 */

export const Picture = ({
  images,
  altText,
  imgTagFallbackConfig = DEFAULT_IMG_TAG_FALLBACK_CONFIG,
  className,
  isLazy,
  dataCy
}) => {
  const [fallbackImgSrc, setFallbackImgSrc] = useState(null);

  useEffect(() => {
    if (!fallbackImgSrc) {
      const defaultImage = images.find(
        (image) =>
          image.resolution === imgTagFallbackConfig.resolution &&
          image.format === imgTagFallbackConfig.format &&
          image.size.id === imgTagFallbackConfig.size
      );

      if (defaultImage?.file) {
        setFallbackImgSrc(defaultImage.file);
      }
    }
  }, [images, imgTagFallbackConfig, fallbackImgSrc]);

  if (!images.length) {
    return false;
  }

  return (
    <picture>
      <SourceTags images={images} />
      <ImgTag
        src={fallbackImgSrc}
        className={className}
        altText={altText}
        isLazy={isLazy}
        dataCy={dataCy}
      />
    </picture>
  );
};

Picture.defaultProps = {
  imgTagFallbackConfig: DEFAULT_IMG_TAG_FALLBACK_CONFIG,
  altText: '',
  className: '',
  isLazy: false,
  dataCy: null
};

Picture.propTypes = {
  images: PropTypes.arrayOf(
    PropTypes.shape({
      file: PropTypes.string.isRequired,
      format: PropTypes.oneOf(['png', 'webp', 'jpg']).isRequired,
      resolution: PropTypes.oneOf(['1x', '2x']).isRequired,
      size: PropTypes.shape({
        id: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl']),
        value: PropTypes.string,
        minVw: PropTypes.string
      }).isRequired
    })
  ).isRequired,
  imgTagFallbackConfig: PropTypes.shape({
    size: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl']),
    format: PropTypes.oneOf(['png', 'webp', 'jpg']),
    resolution: PropTypes.oneOf(['1x', '2x'])
  }),
  altText: PropTypes.string,
  className: PropTypes.string,
  isLazy: PropTypes.bool,
  dataCy: PropTypes.string
};

export default Picture;
